'use client';
import type {
  Material,
  MaterialWithSoftTag,
} from '@/features/match-maker/utils/match-maker.types';
import { useEffect, useMemo, useState } from 'react';
import { fetchTagValues } from './fetch-tag-values.api';
import {
  //MeasurementUnitEnum,
  type ProductionPeriod,
  type TagValueData,
} from '../utils/types';

type UseTagValuesReturn = {
  material: Material | null;
  productionPeriod: ProductionPeriod | null;
  onlyPrimaryTags?: boolean;
  materialUnits: Record<string, string>;
};

export const useTagValues = ({
  material,
  productionPeriod,
  materialUnits,
  onlyPrimaryTags = false,
}: UseTagValuesReturn) => {
  const [tagValues, setTagValues] = useState<Record<string, TagValueData>>({});
  const [isTagValuesLoading, setIsTagValuesLoading] = useState(false);
  const [tagValuesError, setTagValuesError] = useState<string | null>(null);
  const listOfAllTags = useMemo(() => {
    if (!material) {
      return [];
    }
    const materialTags = (material.tags || []).map(tag => ({
      ...tag,
      unit: tag.unit || materialUnits[material.material_code] || '',
    }));
    const componentTags =
      (material.components || []).reduce((acc, c) => {
        (c.tags || []).forEach(element => {
          acc.push({
            ...element,
            unit: element.unit || materialUnits[c.component] || '',
          });
        });
        return acc;
      }, [] as Array<MaterialWithSoftTag>) || [];
    const routingTags =
      (material.routings || []).reduce((acc, r) => {
        (r.tags || []).forEach(element => {
          acc.push({
            ...element,
            unit: element.unit || materialUnits[r.workcenter] || '',
          });
        });
        return acc;
      }, [] as Array<MaterialWithSoftTag>) || [];
    return [...materialTags, ...componentTags, ...routingTags].filter(
      tag => !onlyPrimaryTags || tag.priority === 1
    );
  }, [material, onlyPrimaryTags, materialUnits]);

  useEffect(() => {
    if (!productionPeriod || listOfAllTags.length === 0) {
      setTagValues({});
      setTagValuesError(null);
      setIsTagValuesLoading(false);
      return;
    }

    const period_start = productionPeriod.start_time;
    const period_end = productionPeriod.end_time;
    let isMounted = true;

    const loadTagValues = async () => {
      setIsTagValuesLoading(true);
      setTagValuesError(null);

      const tagIds = listOfAllTags.map(tag => tag.id);
      const tagTimeSeriesMap = listOfAllTags.reduce(
        (acc, cur) => {
          acc[cur.timeseries_id] = cur;
          return acc;
        },
        {} as Record<string, MaterialWithSoftTag>
      );

      let tagValues: Array<{ tagId: string; data: TagValueData }> = [];

      try {
        const tagValuesResponse = await fetchTagValues({
          tag_ids: tagIds,
          period_start,
          period_end,
          latest_value: true,
        });
        tagValues = tagValuesResponse.successes.map(item => {
          return {
            tagId: tagTimeSeriesMap[item.id].id,
            data: {
              value: item.data_points[0]?.value ?? 0,
              unit: item.unit || tagTimeSeriesMap[item.id].unit,
              timestamp: item.data_points[0]?.timestamp ?? '',
              hasError: (tagValuesResponse.errors || []).length > 0,
            },
          };
        });
      } catch (error) {
        console.error('Failed to fetch tag values:', error);
        tagValues = listOfAllTags.map(tag => ({
          tagId: tag.id,
          data: {
            value: 0,
            unit: tag.unit,
            timestamp: '',
            hasError: true,
          },
        }));
      }

      if (!isMounted) {
        return;
      }

      const nextTagValues = Object.fromEntries(
        tagValues.map(({ tagId, data }) => [tagId, data])
      ) as Record<string, TagValueData>;
      const hasError = tagValues.some(({ data }) => data.hasError);

      setTagValues(nextTagValues);
      setTagValuesError(
        hasError ? 'Some tag values could not be loaded.' : null
      );
      setIsTagValuesLoading(false);
    };

    void loadTagValues();

    return () => {
      isMounted = false;
    };
  }, [listOfAllTags, productionPeriod, onlyPrimaryTags]);

  return {
    tagValues,
    tagValuesError,
    isTagValuesLoading,
  };
};
